import type { ReactNode } from 'react'

import { DetailSection } from '../detail/DetailShell'

interface ReportTableProps {
  title: string
  isPending: boolean
  isError: boolean
  isEmpty: boolean
  emptyLabel: string
  onRetry: () => void
  caption?: string | undefined
  footer?: ReactNode
  action?: ReactNode
  /** When true, render the gated states WITHOUT the `DetailSection` card chrome (the owning card draws
   *  its own surface — Coverage's `SurveyStatusCard`). Default false = today's titled-card look. */
  bare?: boolean
  children: ReactNode
}

/** Shared gating shell for the report sections: skeleton → error+retry → distinct empty → content. */
export function ReportTable({
  title,
  isPending,
  isError,
  isEmpty,
  emptyLabel,
  onRetry,
  caption,
  footer,
  action,
  bare = false,
  children,
}: ReportTableProps) {
  const body = isPending ? (
    <div className="space-y-2 py-2" aria-hidden>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-7 animate-pulse rounded bg-slate-900/5" />
      ))}
    </div>
  ) : isError ? (
    <div className="text-muted-text py-4 text-center text-[13px]">
      Couldn't load this report.{' '}
      <button type="button" onClick={onRetry} className="font-semibold text-blue-600 underline">Retry</button>
    </div>
  ) : isEmpty ? (
    <div className="text-muted-text py-4 text-center text-[13px]">{emptyLabel}</div>
  ) : (
    <>
      {children}
      {caption && <p className="text-muted-text mt-2 text-[11px]">{caption}</p>}
      {footer}
    </>
  )

  if (bare) {
    return body
  }

  return (
    <DetailSection title={title} action={action}>
      {body}
    </DetailSection>
  )
}
